import { Table } from "dexie"

// Client-side encryption for sensitive draft values (SSNs). The AES-GCM key is
// non-extractable and lives in the same IndexedDB, so this defends against
// casual DB inspection and backup exfiltration — not a same-origin attacker.
// The short sensitive TTL (2h vs 14d for the rest of the draft) is the real
// exposure bound; when WebCrypto is unavailable the values are simply dropped.

export const INTAKE_FORM_SENSITIVE_TTL_MS = 2 * 60 * 60 * 1000

const DRAFT_KEY_ID = "draft-key"

export interface DraftKeyRecord {
  id: string
  key: CryptoKey
}

export interface EncryptedSensitiveValues {
  iv: string
  data: string
}

function subtleCrypto(): SubtleCrypto | null {
  if (typeof crypto === "undefined" || !crypto.subtle) return null
  return crypto.subtle
}

function toBase64(bytes: Uint8Array): string {
  let binary = ""
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })
  return btoa(binary)
}

function fromBase64(encoded: string): Uint8Array {
  const binary = atob(encoded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

export async function getOrCreateDraftKey(keys: Table<DraftKeyRecord>): Promise<CryptoKey | null> {
  const subtle = subtleCrypto()
  if (!subtle) return null

  try {
    const existing = await keys.get(DRAFT_KEY_ID)
    if (existing) return existing.key

    const key = await subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"])
    // add() is atomic on the primary key: when two tabs race to mint the first
    // key, the loser's insert fails and both end up using the persisted winner
    // — a split key would make one tab's ciphertext undecryptable.
    try {
      await keys.add({ id: DRAFT_KEY_ID, key })
      return key
    } catch {
      const winner = await keys.get(DRAFT_KEY_ID)
      return winner?.key ?? null
    }
  } catch {
    return null
  }
}

export async function encryptSensitiveValues(
  key: CryptoKey,
  values: Record<string, unknown>
): Promise<EncryptedSensitiveValues | null> {
  const subtle = subtleCrypto()
  if (!subtle) return null

  try {
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const encoded = new TextEncoder().encode(JSON.stringify(values))
    const encrypted = await subtle.encrypt({ name: "AES-GCM", iv }, key, encoded)
    return { iv: toBase64(iv), data: toBase64(new Uint8Array(encrypted)) }
  } catch {
    return null
  }
}

export async function decryptSensitiveValues(
  key: CryptoKey,
  encrypted: EncryptedSensitiveValues
): Promise<Record<string, unknown> | null> {
  const subtle = subtleCrypto()
  if (!subtle) return null

  try {
    const decrypted = await subtle.decrypt(
      { name: "AES-GCM", iv: fromBase64(encrypted.iv).buffer as ArrayBuffer },
      key,
      fromBase64(encrypted.data).buffer as ArrayBuffer
    )
    const parsed: unknown = JSON.parse(new TextDecoder().decode(decrypted))
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return null
    return parsed as Record<string, unknown>
  } catch {
    return null
  }
}

// Sensitive paths are flattened field references from the runtime: a top-level
// field key ("ssn") or a repeater cell ("dependents[0].ssn").
const SENSITIVE_PATH_RE = /^([a-z][a-z0-9_]*)(?:\[(\d+)\]\.([a-z][a-z0-9_]*))?$/

interface ParsedPath {
  fieldKey: string
  rowIndex: number | null
  subKey: string | null
}

function parsePath(path: string): ParsedPath | null {
  const match = SENSITIVE_PATH_RE.exec(path)
  if (!match) return null
  return {
    fieldKey: match[1],
    rowIndex: match[2] === undefined ? null : Number(match[2]),
    subKey: match[3] ?? null,
  }
}

function containerFor(answers: Record<string, unknown>, parsed: ParsedPath): Record<string, unknown> | null {
  if (parsed.rowIndex === null) return answers

  const rows = answers[parsed.fieldKey]
  if (!Array.isArray(rows)) return null
  const row = rows[parsed.rowIndex]
  if (typeof row !== "object" || row === null || Array.isArray(row)) return null
  return row as Record<string, unknown>
}

export function takeSensitiveValue(answers: Record<string, unknown>, path: string): unknown {
  const parsed = parsePath(path)
  if (!parsed) return undefined
  const container = containerFor(answers, parsed)
  if (!container) return undefined

  const key = parsed.subKey ?? parsed.fieldKey
  const value = container[key]
  if (value == null || value === "") return undefined
  container[key] = null
  return value
}

export function restoreSensitiveValue(answers: Record<string, unknown>, path: string, value: unknown): void {
  const parsed = parsePath(path)
  if (!parsed) return
  const container = containerFor(answers, parsed)
  if (!container) return

  container[parsed.subKey ?? parsed.fieldKey] = value
}
